import { useState, useEffect, useRef } from 'react';

interface Faq {
  question: string;
  answer: string;
}

interface FaqsSectionProps {
  faqs: Faq[];
  seoTitle?: string;
  seoText?: string;
  mapEmbed?: string;
  locationName?: string;
  showLocationSpecificContent?: boolean;
  includeSchema?: boolean;
}

export default function FaqsSection({
  faqs,
  locationName,
  showLocationSpecificContent = true,
  includeSchema = true,
}: FaqsSectionProps) {
  // Initialize state based on the length of the faqs array passed in via props.
  const [openIndexes, setOpenIndexes] = useState<boolean[]>(Array(faqs.length).fill(false));
  const faqContainerRef = useRef<HTMLDivElement>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndexes((prev) => {
      const newState = [...prev];
      newState[index] = !newState[index];
      return newState;
    });
  };

  // Clean up problematic ARIA attributes from WordPress content
  // WordPress Ultimate Addons for Beaver Builder injects divs with ARIA roles
  // that don't have required children, causing accessibility errors
  useEffect(() => {
    if (!faqContainerRef.current) return;

    // Function to clean up ARIA attributes
    const cleanupARIA = () => {
      if (!faqContainerRef.current) return;

      // Find all WordPress FAQ divs with problematic ARIA roles
      const problematicDivs = faqContainerRef.current.querySelectorAll(
        '.uabb-faq-item[role], .uabb-faq-content[role], [class*="uabb-faq"][role]',
      );

      problematicDivs.forEach((div) => {
        const element = div as HTMLElement;
        const role = element.getAttribute('role');

        // Remove all problematic roles that require specific children
        const problematicRoles = [
          'region', // Requires landmarks or other regions
          'group', // Requires group items
          'list', // Requires listitem children
          'navigation', // Requires nav items
          'tablist', // Requires tab children
          'menu', // Requires menuitem children
          'menubar', // Requires menuitem children
        ];

        if (role && problematicRoles.includes(role)) {
          element.removeAttribute('role');
        }
      });

      // Also remove any aria-* attributes from WordPress divs
      const allWordPressDivs = faqContainerRef.current.querySelectorAll(
        '.uabb-faq-item, .uabb-faq-content, [class*="uabb-faq"]',
      );

      allWordPressDivs.forEach((div) => {
        const element = div as HTMLElement;
        const ariaAttributes = Array.from(element.attributes)
          .filter((attr) => attr.name.startsWith('aria-'))
          .map((attr) => attr.name);

        ariaAttributes.forEach((attr) => {
          element.removeAttribute(attr);
        });
      });
    };

    // Run cleanup immediately and after a short delay to catch async WordPress content
    cleanupARIA();
    const timeoutId = setTimeout(cleanupARIA, 100);

    // Use MutationObserver to catch dynamically added WordPress content
    const observer = new MutationObserver(() => {
      cleanupARIA();
    });

    if (faqContainerRef.current) {
      observer.observe(faqContainerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['role', 'aria-expanded', 'aria-hidden'],
      });
    }

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [faqs, openIndexes]);

  return (
    <section
      className="flex w-full justify-start bg-transparent py-4"
      {...(includeSchema && {
        itemScope: true,
        itemType: 'https://schema.org/FAQPage',
      })}
    >
      {/* FAQ Container */}
      <div className="flex w-full flex-col items-center gap-2 rounded-lg bg-white p-0 md:max-w-2/3">
        {/* Header */}
        <div className="flex w-full flex-col items-start gap-4 rounded-lg bg-white px-0 py-2">
          <div className="flex w-full flex-row items-center gap-5 px-0 py-0">
            {/* FAQs Icon - Only show on location pages */}
            {showLocationSpecificContent && (
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
                <img src="/locations/FAQs.svg" alt="FAQ section" className="h-6 w-6" />
              </div>
            )}

            {/* Dynamic Title */}
            <h2 className={`font-helvetica-neue flex-1 text-xl font-medium text-black`}>
              {showLocationSpecificContent && locationName
                ? `Dispensary in ${locationName.replace(/^Catalyst /, '').replace(' Dispensary', '')} FAQs`
                : 'Frequently Asked Questions'}
            </h2>
          </div>

          {/* Dynamic Intro Text - Only show on location pages */}
          {showLocationSpecificContent && locationName && (
            <p className="font-helvetica-neue ml-10 text-base font-normal text-gray-700">
              We can't wait to welcome you to our dispensary in{' '}
              {locationName.replace(/^Catalyst /, '').replace(' Dispensary', '')}. As you plan your
              visit, here are a few helpful things to keep in mind.
            </p>
          )}
        </div>

        {/* FAQ List */}
        <div
          ref={faqContainerRef}
          role="region"
          aria-label="Frequently asked questions"
          className={`flex w-full flex-col items-start gap-4 py-2 ps-2 pr-4 ${showLocationSpecificContent ? 'ml-[36px]' : 'ml-0'}`}
        >
          {faqs.map((faq, index: number) => (
            <article
              key={index}
              className="flex w-full flex-col items-center p-0"
              {...(includeSchema && {
                itemScope: true,
                itemProp: 'mainEntity',
                itemType: 'https://schema.org/Question',
              })}
            >
              {/* Question Header */}
              <button
                onClick={() => toggleFAQ(index)}
                className="flex w-full cursor-pointer flex-row items-center justify-between gap-8 rounded-lg px-0 py-2 text-left transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
                aria-expanded={openIndexes[index]}
                aria-controls={`faq-answer-${index}`}
                aria-label={`Toggle answer for: ${faq.question}`}
              >
                {/* Question Text */}
                <div className="flex-1">
                  <h3
                    id={`faq-question-${index}`}
                    className="font-helvetica-neue text-lg leading-7 font-medium text-gray-600"
                    {...(includeSchema && { itemProp: 'name' })}
                  >
                    {faq.question}
                  </h3>
                </div>

                {/* Toggle Button */}
                <div
                  className="flex h-3 w-3 flex-shrink-0 items-center justify-center"
                  aria-hidden="true"
                >
                  {/* Plus Icon (when closed) */}
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    className={`transition-opacity duration-200 ${openIndexes[index] ? 'opacity-0' : 'opacity-100'}`}
                    aria-hidden="true"
                  >
                    {/* Horizontal line */}
                    <line
                      x1="0"
                      y1="6"
                      x2="12"
                      y2="6"
                      stroke="#0F172A"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    {/* Vertical line */}
                    <line
                      x1="6"
                      y1="0"
                      x2="6"
                      y2="12"
                      stroke="#0F172A"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>

                  {/* Minus Icon (when open) */}
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    className={`absolute transition-opacity duration-200 ${openIndexes[index] ? 'opacity-100' : 'opacity-0'}`}
                    aria-hidden="true"
                  >
                    {/* Horizontal line only */}
                    <line
                      x1="0"
                      y1="6"
                      x2="12"
                      y2="6"
                      stroke="#0F172A"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </button>

              {/* Answer */}
              <div
                id={`faq-answer-${index}`}
                aria-labelledby={`faq-question-${index}`}
                className={`flex w-full flex-row items-start overflow-hidden transition-all duration-300 ease-in-out ${
                  openIndexes[index] ? 'max-h-96 pt-1 pr-12' : 'max-h-0'
                }`}
                {...(includeSchema && {
                  itemScope: true,
                  itemProp: 'acceptedAnswer',
                  itemType: 'https://schema.org/Answer',
                })}
              >
                <div
                  className="font-helvetica-neue flex-1 text-lg leading-[26px] font-light tracking-[0.02em] text-[#0F172A]"
                  {...(includeSchema && { itemProp: 'text' })}
                  dangerouslySetInnerHTML={{ __html: faq.answer }}
                />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
